import type { ReactNode } from 'react';
import { Link, useLocation } from 'react-router';
import { token } from '@bp-files/ui';
import { APP_COPY } from '@/copy/app.copy';
import { FoldersIcon } from '@/components/FoldersIcon';
import { domainOf, SETTINGS_HOME } from '@/surfaces/surfaces';

/**
 * The rail's domain switcher: Files at the top, Settings just above the sidebar
 * toggle. `SurfaceNav` reads the same `domainOf(pathname)` this uses, so the
 * rail and the sidebar can never disagree about which domain is current
 * (ADR-0007).
 *
 * **The Settings icon is no longer gated on `isAdmin`, and it no longer lands on
 * Spaces** (ADR-0064). Both facts have the same cause: Settings used to be
 * four admin surfaces and nothing else, so showing a member the gear meant
 * showing them a door to a column of things they would be told they may not see.
 * It now opens on General, which is everybody's, with Passkeys and Signature
 * beside it — three pages that were already theirs and were reachable only from
 * the account menu.
 *
 * The prop went with the gate. `SETTINGS_HOME` rather than a literal, because
 * `SurfaceNav`'s wordmark spaces to the same place and these two must not be able
 * to drift apart. Files always lands on the shared-space grid.
 */
export function DomainRail() {
  const domain = domainOf(useLocation().pathname);

  return (
    <>
      <DomainIcon to="/files" label={APP_COPY.rail.files} active={domain === 'files'}>
        {/*
          The stacked pair, matching the glyph on every space it leads to. It
          drew a single `folder` until the space list gained an icon of its own,
          at which point the rail was naming the level below the one it opens —
          `FoldersIcon`'s own header has the argument.
        */}
        <FoldersIcon size={20} />
      </DomainIcon>

      <div className="flex-1" />

      <DomainIcon to={SETTINGS_HOME} label={APP_COPY.rail.settings} active={domain === 'settings'}>
        <SettingsIcon />
      </DomainIcon>
    </>
  );
}

/**
 * The rail's domain switch as a bottom tab bar, for the mobile width where the
 * rail itself is gone (`AppShell`'s `bottomNav`, shown only below `sm`). Same
 * two destinations and the same `domainOf`, so it can no more disagree with the
 * sidebar than the rail can — it is the rail laid on its side, with the labels
 * shown because a full-width bar has room the 64px column never did.
 *
 * The rail's left-edge active indicator does not survive the rotation, so the
 * active tab is marked by colour and `aria-current` alone.
 */
export function DomainTabBar() {
  const domain = domainOf(useLocation().pathname);

  return (
    <div className="flex items-stretch">
      <DomainTab to="/files" label={APP_COPY.rail.files} active={domain === 'files'}>
        <FoldersIcon size={20} />
      </DomainTab>

      <DomainTab to={SETTINGS_HOME} label={APP_COPY.rail.settings} active={domain === 'settings'}>
        <SettingsIcon />
      </DomainTab>
    </div>
  );
}

function DomainTab({
  to,
  label,
  active,
  children,
}: {
  to: string;
  label: string;
  active: boolean;
  children: ReactNode;
}) {
  return (
    <Link
      to={to}
      aria-current={active ? 'true' : undefined}
      className="flex flex-1 flex-col items-center justify-center gap-1 py-2 text-xs font-medium transition-colors hover:bg-elevated"
      style={{ color: active ? token('bp-blue-rail') : token('bp-text-muted') }}
    >
      {children}
      <span>{label}</span>
    </Link>
  );
}

function DomainIcon({
  to,
  label,
  active,
  children,
}: {
  to: string;
  label: string;
  active: boolean;
  children: ReactNode;
}) {
  return (
    <div className="relative flex w-full items-center justify-center">
      {active ? (
        <span
          aria-hidden="true"
          className="absolute left-1.5 h-5 rounded-full"
          style={{ width: token('rail-indicator'), background: token('bp-blue-rail') }}
        />
      ) : null}
      <Link
        to={to}
        aria-label={label}
        aria-current={active ? 'true' : undefined}
        className="flex items-center justify-center rounded-md p-2 transition-colors hover:bg-elevated"
        style={{ color: active ? token('bp-blue-rail') : token('bp-text-muted') }}
      >
        {children}
      </Link>
    </div>
  );
}

function SettingsIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      width={20}
      height={20}
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M12.22 2h-.44a2 2 0 0 0-2 2v.18a2 2 0 0 1-1 1.73l-.43.25a2 2 0 0 1-2 0l-.15-.08a2 2 0 0 0-2.73.73l-.22.38a2 2 0 0 0 .73 2.73l.15.1a2 2 0 0 1 1 1.72v.51a2 2 0 0 1-1 1.74l-.15.09a2 2 0 0 0-.73 2.73l.22.38a2 2 0 0 0 2.73.73l.15-.08a2 2 0 0 1 2 0l.43.25a2 2 0 0 1 1 1.73V20a2 2 0 0 0 2 2h.44a2 2 0 0 0 2-2v-.18a2 2 0 0 1 1-1.73l.43-.25a2 2 0 0 1 2 0l.15.08a2 2 0 0 0 2.73-.73l.22-.39a2 2 0 0 0-.73-2.73l-.15-.08a2 2 0 0 1-1-1.74v-.5a2 2 0 0 1 1-1.74l.15-.09a2 2 0 0 0 .73-2.73l-.22-.38a2 2 0 0 0-2.73-.73l-.15.08a2 2 0 0 1-2 0l-.43-.25a2 2 0 0 1-1-1.73V4a2 2 0 0 0-2-2z" />
      <circle cx="12" cy="12" r="3" />
    </svg>
  );
}
